import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import LoginModal from '@/components/LoginModal'
import { 
  MessageSquare, 
  Zap, 
  Shield, 
  BarChart3,
  CheckCircle,
  ArrowRight,
  Globe,
  Users,
  Settings,
  Code,
  Smartphone,
  Mail,
  Bell,
  Lock,
  Database,
  Cloud,
  Network,
  Target,
  TrendingUp
} from 'lucide-react'

// 产品功能分类
const featureCategories = {
  core: {
    title: '核心功能',
    description: '基础短信发送能力',
    features: [
      {
        icon: MessageSquare,
        title: '短信发送',
        description: '支持验证码、通知、营销等多种短信类型',
        details: [
          '验证码短信：登录、注册、支付验证',
          '通知短信：订单状态、系统通知',
          '营销短信：活动推广、会员关怀',
          '国际短信：支持200+国家和地区'
        ],
        color: 'text-blue-600'
      },
      {
        icon: Zap,
        title: '极速发送',
        description: '毫秒级响应，高并发处理能力',
        details: [
          '平均发送延迟 < 3秒',
          '支持10万+ QPS并发',
          '99.9% 到达率保障',
          '智能路由选择'
        ],
        color: 'text-yellow-600'
      },
      {
        icon: Shield,
        title: '安全防护',
        description: '企业级安全防护体系',
        details: [
          '数据加密传输',
          'IP白名单控制',
          '签名审核机制',
          '内容安全检测'
        ],
        color: 'text-green-600'
      },
      {
        icon: BarChart3,
        title: '实时监控',
        description: '全链路监控和数据分析',
        details: [
          '实时发送状态',
          '详细发送报告',
          '失败原因分析',
          '数据可视化展示'
        ],
        color: 'text-purple-600'
      }
    ]
  },
  advanced: {
    title: '高级功能',
    description: '提升业务效率的智能功能',
    features: [
      {
        icon: Target,
        title: '智能路由',
        description: '自动选择最优通道，提升发送成功率',
        details: [
          '多通道智能切换',
          '实时通道质量监控',
          '自动故障转移',
          '成本优化算法'
        ],
        color: 'text-red-600'
      },
      {
        icon: Database,
        title: '模板管理',
        description: '灵活的短信模板管理系统',
        details: [
          '模板在线编辑',
          '变量动态替换',
          '模板审核流程',
          '版本历史管理'
        ],
        color: 'text-indigo-600'
      },
      {
        icon: Bell,
        title: 'Webhook通知',
        description: '实时状态回调通知',
        details: [
          '发送状态实时推送',
          '自定义回调地址',
          '重试机制保障',
          '签名验证安全'
        ],
        color: 'text-orange-600'
      },
      {
        icon: Users,
        title: '多用户管理',
        description: '支持团队协作和权限控制',
        details: [
          '多级用户权限',
          'API密钥管理',
          '操作日志审计',
          '团队协作功能'
        ],
        color: 'text-teal-600'
      }
    ]
  },
  integration: {
    title: '集成方案',
    description: '简单易用的集成方式',
    features: [
      {
        icon: Code,
        title: 'RESTful API',
        description: '标准REST API，支持多种编程语言',
        details: [
          'HTTP/HTTPS协议',
          'JSON数据格式',
          '多语言SDK',
          '详细API文档'
        ],
        color: 'text-blue-600'
      },
      {
        icon: Settings,
        title: 'SDK集成',
        description: '官方SDK，快速集成',
        details: [
          'Java/Python/PHP/Node.js',
          '一键安装配置',
          '代码示例丰富',
          '持续更新维护'
        ],
        color: 'text-green-600'
      },
      {
        icon: Cloud,
        title: '云服务集成',
        description: '与主流云平台深度集成',
        details: [
          '阿里云/腾讯云/华为云',
          'AWS/Azure/GCP',
          '容器化部署',
          '弹性伸缩支持'
        ],
        color: 'text-purple-600'
      },
      {
        icon: Network,
        title: '第三方集成',
        description: '与主流系统无缝对接',
        details: [
          'CRM系统集成',
          'ERP系统对接',
          '电商平台插件',
          '自定义集成方案'
        ],
        color: 'text-pink-600'
      }
    ]
  }
}

// 技术规格
const technicalSpecs = [
  {
    category: '性能指标',
    specs: [
      { name: '并发处理能力', value: '10万+ QPS' },
      { name: '平均响应时间', value: '< 3秒' },
      { name: '服务可用性', value: '99.99%' },
      { name: '数据存储', value: '6个月' }
    ]
  },
  {
    category: '安全标准',
    specs: [
      { name: '传输加密', value: 'TLS 1.3' },
      { name: '数据加密', value: 'AES-256' },
      { name: '访问控制', value: 'OAuth 2.0' },
      { name: '合规认证', value: 'ISO 27001' }
    ]
  },
  {
    category: '服务支持',
    specs: [
      { name: '技术支持', value: '7x24小时' },
      { name: '响应时间', value: '< 1小时' },
      { name: '文档语言', value: '中英文' },
      { name: '培训服务', value: '免费提供' }
    ]
  }
]

export default function FeaturesPage() {
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [activeCategory, setActiveCategory] = useState('core')

  return (
    <div className="space-y-20">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center space-y-8">
            <div className="space-y-4">
              <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
                🚀 功能强大，简单易用
              </Badge>
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight">
                全面的
                <span className="text-blue-600">短信服务</span>
                功能
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto">
                从基础发送到高级功能，从简单集成到复杂场景，我们提供完整的短信解决方案
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="text-lg px-8 py-3"
                onClick={() => setShowLoginModal(true)}
              >
                免费试用
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button variant="outline" size="lg" className="text-lg px-8 py-3" asChild>
                <Link to="/docs">
                  查看文档
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* 功能分类导航 */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Tabs value={activeCategory} onValueChange={setActiveCategory} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-12">
            <TabsTrigger value="core">核心功能</TabsTrigger>
            <TabsTrigger value="advanced">高级功能</TabsTrigger>
            <TabsTrigger value="integration">集成方案</TabsTrigger>
          </TabsList>

          {Object.entries(featureCategories).map(([key, category]) => (
            <TabsContent key={key} value={key} className="space-y-8">
              <div className="text-center space-y-4">
                <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
                  {category.title}
                </h2>
                <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                  {category.description}
                </p>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {category.features.map((feature, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-start space-x-4">
                        <div className={`w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0`}>
                          <feature.icon className={`h-6 w-6 ${feature.color}`} />
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-xl mb-2">{feature.title}</CardTitle>
                          <CardDescription className="text-base">
                            {feature.description}
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {feature.details.map((detail, detailIndex) => (
                          <li key={detailIndex} className="flex items-center space-x-2 text-sm text-gray-600">
                            <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </section>

      {/* 技术规格 */}
      <section className="bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
              技术规格
            </h2>
            <p className="text-xl text-gray-600">
              企业级技术标准，保障服务稳定可靠
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {technicalSpecs.map((spec, index) => (
              <Card key={index}>
                <CardHeader>
                  <CardTitle className="text-xl text-center">{spec.category}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {spec.specs.map((item, itemIndex) => (
                      <div key={itemIndex} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                        <span className="text-gray-600">{item.name}</span>
                        <Badge variant="secondary">{item.value}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* 使用场景 */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center space-y-4 mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
            应用场景
          </h2>
          <p className="text-xl text-gray-600">
            覆盖各行各业，满足不同业务需求
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: Smartphone, title: '移动应用', desc: 'APP注册登录验证' },
            { icon: Globe, title: '电商平台', desc: '订单状态通知' },
            { icon: Mail, title: '金融服务', desc: '交易安全验证' },
            { icon: Users, title: '企业服务', desc: '内部系统通知' },
            { icon: Bell, title: '在线教育', desc: '课程提醒通知' },
            { icon: Target, title: '营销推广', desc: '活动信息推送' },
            { icon: Lock, title: '安全验证', desc: '身份认证确认' },
            { icon: TrendingUp, title: '数据分析', desc: '业务数据报告' }
          ].map((scenario, index) => (
            <Card key={index} className="text-center hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="mx-auto w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center mb-4">
                  <scenario.icon className="h-6 w-6 text-blue-600" />
                </div>
                <CardTitle className="text-lg">{scenario.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription>{scenario.desc}</CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center space-y-8">
            <h2 className="text-3xl lg:text-4xl font-bold">
              开始使用我们的功能
            </h2>
            <p className="text-xl opacity-90 max-w-2xl mx-auto">
              立即注册，体验完整的短信服务功能，让您的业务更高效
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                variant="secondary" 
                className="text-lg px-8 py-3"
                onClick={() => setShowLoginModal(true)}
              >
                立即开始
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" variant="outline" className="text-lg px-8 py-3 border-white text-white hover:bg-white hover:text-blue-600" asChild>
                <Link to="/pricing">
                  查看价格
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* 登录 Modal */}
      <LoginModal 
        open={showLoginModal} 
        onOpenChange={setShowLoginModal} 
      />
    </div>
  )
}
